import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';
import { HitchDiagram } from '../../components/Diagrams';

export default function Kugletryk() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Kørekort og vægt", path: "/trailer-regler" },
    { name: "Kugletryk", path: "/kugletryk" }
  ];
  const tocItems = [
    { id: "hvad-er-kugletryk", title: "Hvad er kugletryk?" },
    { id: "hvor-meget-maa-bilen-baere", title: "Hvor meget må bilen bære? (S-værdi)" },
    { id: "farerne-ved-forkert-kugletryk", title: "Farerne ved for højt og for lavt kugletryk" },
    { id: "saadan-maaler-du", title: "Sådan måler du kugletrykket nemt" },
    { id: "korrekt-laesning", title: "Principper for korrekt læsning af traileren" }
  ];

  const faq = [
    {
      q: "Hvad er det normale kugletryk på en almindelig personbil?",
      a: "For almindelige personbiler ligger det maksimale tilladte kugletryk typisk mellem 50 og 75 kg, mens større SUV'er og varebiler kan være godkendt til 85–140 kg. Det anbefales, at det faktiske kugletryk ligger tæt på bilens godkendte maksimum uden at overskride det."
    },
    {
      q: "Hvorfor er et for lavt eller negativt kugletryk livsfarligt?",
      a: "Hvis traileren er bagtung, løfter trailerens trækstang opad i bilens anhængertræk. Det fjerner trykket fra bilens baghjul, hvilket ved landevejshastighed udløser kraftig og ukontrollabel slinger, som kan få hele vogntoget til at vælte."
    },
    {
      q: "Hvor finder jeg det tilladte kugletryk for min bil?",
      a: "Det tilladte kugletryk er indpræget på anhængertrækkets typeskilt som værdien 'S' (Stützlast / lodret belastning i kg). Det fremgår også ofte af bilens instruktionsbog."
    },
    {
      q: "Kan man måle kugletryk med en almindelig badevægt?",
      a: "Ja, det kan du sagtens. Placér en badevægt under trailerens trækstang, sæt en træklods eller stump lægte mellem vægten og koblingshovedet, og sænk næsehjulet, så trailerens kobling hviler på træklodsen i samme højde som bilens træk."
    }
  ];

  const sources = [
    { title: "Bekendtgørelse om køretøjers indretning og udstyr (BEK nr. 154 af 28/01/2021)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2021/154" },
    { title: "Dimensionsbekendtgørelsen (BEK nr. 577 af 30/03/2021)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2021/577" },
    { title: "Færdselsloven § 82 om forsvarlig belæsning (LBK nr. 1324 af 18/09/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1324" }
  ];

  return (
    <>
      <SEO 
        title="Kugletryk på trailer: Hvor meget må bilen bære?" 
        description="Alt om kugletryk på trailer: hvad det er, bilens tilladte S-værdi, måling med badevægt og hvordan du undgår farlig slinger." 
        pathname="/kugletryk"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Kugletryk: Hvad det er, og hvordan du læsser rigtigt
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          Kugletryk er den lodrette vægt, som trailerens koblingshoved trykker ned på bilens anhængerkrog. For almindelige personbiler skal kugletrykket typisk ligge mellem 50 og 75 kg for at sikre stabil og sikker kørsel.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Et for lavt eller negativt kugletryk – hvor traileren er bagtung – er en af de farligste fejl, en bilist kan begå, da det fjerner vejgreb fra bilens baghjul og fører til voldsom slinger. Omvendt overbelaster et for højt kugletryk bilens baghjulsophæng og forringer styreevnen på forhjulene.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Kugletryk i korte træk">
          <p className="m-0">
            Kugletrykket må <strong>aldrig være negativt</strong> (løfte op i trækket). Sigt altid efter at ramme bilens tilladte maksimum (S-værdien), som typisk er 50–75 kg for almindelige personbiler.
          </p>
        </Callout>

        <h2 id="hvad-er-kugletryk" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvad er kugletryk?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når en trailer kobles på en bil, overføres trailerens samlede vægt til vejen via to punkter: dels gennem trailerens egne hjul (akseltrykket), og dels gennem bilens anhængertræk (kugletrykket).
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Kugletrykket er alene den lodrette kraft, der trykker direkte ned i koblingskuglen – for almindelige personbiler ligger det typisk mellem 50 og 75 kg. Det har ikke noget at gøre med, hvor mange kilo bilen må trække horisontalt fremad. Kugletrykket tæller teknisk set med som last i trækbilen.
        </p>

        <HitchDiagram caption="Figur 1: Koblingshovedets lodrette tryk (S-værdi) ned på bilens anhængerkrog." />

        <h2 id="hvor-meget-maa-bilen-baere" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvor meget må bilen bære? (S-værdi)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Både bilens anhængertræk og trailerens koblingshoved er typegodkendt til et maksimalt lodret tryk. Denne værdi betegnes internationalt som <strong>S</strong> (Stützlast):
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Biltype</th>
                <th className="py-2.5 pr-4 font-semibold">Typisk tilladt kugletryk (S)</th>
                <th className="py-2.5 font-semibold">Karakteristika</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Mindre personbiler</td>
                <td className="py-3 pr-4">50 – 60 kg</td>
                <td className="py-3 text-[#5C636B]">Kræver præcis placering af gods</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Mellemklasse og stationcars</td>
                <td className="py-3 pr-4">70 – 80 kg</td>
                <td className="py-3 text-[#5C636B]">Standard for de fleste familiebiler</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Store SUV'er og elbiler</td>
                <td className="py-3 pr-4">80 – 100 kg</td>
                <td className="py-3 text-[#5C636B]">Høj stabilitet ved tunge trailere</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Varebiler og pick-ups</td>
                <td className="py-3 pr-4">100 – 150 kg</td>
                <td className="py-3 text-[#5C636B]">Beregnet til store maskintrailere</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Hvis bilens træk er godkendt til 75 kg, og trailerens koblingshoved er godkendt til 100 kg, er det altid den <strong>laveste værdi (75 kg)</strong>, der gælder som den absolutte øvre grænse.
        </p>

        <h2 id="farerne-ved-forkert-kugletryk" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Farerne ved for højt og for lavt kugletryk
        </h2>

        <div className="space-y-4 my-6">
          <div className="p-4 border-l-4 border-[#2E3A46] bg-[#FBFAF6] border border-[#E4E1D8] rounded-[4px]">
            <h3 className="text-[17px] font-bold text-[#2E3A46] mb-1">
              For lavt kugletryk (under 25 kg eller negativt)
            </h3>
            <p className="text-[15px] text-[#1A1C1F] m-0 leading-relaxed">
              Opstår når godset er placeret for langt bagud på ladet. Traileren fungerer som en vægtstang, der vipper bagover og løfter bilens bagende. Det fjerner dæktryk og vejgreb fra trækbilens baghjul, hvilket gør vogntoget ekstremt sårbart over for sidevind, overhaling af lastbiler og små styrekorrektioner. Resultatet er livsfarlig slinger.
            </p>
          </div>

          <div className="p-4 border-l-4 border-[#B58200] bg-[#FBFAF6] border border-[#E4E1D8] rounded-[4px]">
            <h3 className="text-[17px] font-bold text-[#2E3A46] mb-1">
              For højt kugletryk (over bilens S-værdi)
            </h3>
            <p className="text-[15px] text-[#1A1C1F] m-0 leading-relaxed">
              Opstår når det tungeste gods lægges helt forrest mod forsmækken. Bilens bagfjedre presses i bund, forhjulene løftes en smule, hvorved styreevne og bremsekraft forringes, og forlygterne vinkles opad så de blænder modkørende. Samtidig risikerer man mekanisk brud på trækkrogsophænget.
            </p>
          </div>
        </div>

        <h2 id="saadan-maaler-du" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Sådan måler du kugletrykket nemt
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Du behøver ikke specialudstyr for at kontrollere kugletrykket:
        </p>

        <ol className="list-decimal list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Stil traileren på et plant og vandret underlag, og spænd håndbremsen.</li>
          <li>Placér en almindelig personvægt (badevægt) på jorden direkte under trailerens kuglehoved.</li>
          <li>Skær et stykke trælægte til, så længden svarer til højden fra vægten op til bilens trækkugle.</li>
          <li>Sænk trailerens kuglehoved ned på lægten ved hjælp af næsehjulet, indtil næsehjulet svæver frit over jorden.</li>
          <li>Aflæs vægten på badevægten. Vægten viser nu det præcise kugletryk i kg.</li>
        </ol>

        <h2 id="korrekt-laesning" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Principper for korrekt læsning af traileren
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Følg disse grundlæggende råd hver gang du laster:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Det tungeste gods lige foran akslen:</strong> Placer tunge genstande (fliser, grus, maskiner) i bunden af traileren lige foran trailerens hjulaksel.</li>
          <li><strong>Fordel vægten i siderne:</strong> Hold tyngdepunktet i midten af traileren for at undgå ujævn belastning på hjulene.</li>
          <li><strong>Fastgør godset:</strong> Brug godkendte spændestropper. Last, der kan forskubbe sig under bremsning, ændrer kugletrykket under kørslen. Se vores guide til <Link to="/lastsikring" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">lastsikring</Link>.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs også mere om <Link to="/trailer-totalvaegt" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailerens totalvægt og lasteevne</Link> samt færdselsreglerne for <Link to="/trailer-hastighed" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">hastighed med trailer</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
